import { doc, getDoc, setDoc, collection, getDocs } from 'firebase/firestore';

export const ALTERDATA_BASE_URL = 'https://dp.pack.alterdata.com.br/api/v1';

/**
 * Utilitário interno para higienizar CNPJ/CPF (somente dígitos)
 */
export const higienizarDoc = (val) => {
    if (!val) return '';
    return String(val).replace(/\D/g, '');
};

/**
 * Obtém o Token Master do Alterdata configurado no Firestore
 */
export async function obterTokenAlterdata(db, DB_PATH) {
    try {
        const intRef = doc(db, `${DB_PATH}hub_integrations`, 'alterdata_funcionarios_acads');
        const snap = await getDoc(intRef);
        if (snap.exists() && snap.data().headerValue) {
            return snap.data().headerValue;
        }

        // Fallback: verificar se existe na integração legada CadCli
        const cadCliRef = doc(db, `${DB_PATH}hub_integrations`, '1774993644801');
        const cadCliSnap = await getDoc(cadCliRef);
        if (cadCliSnap.exists() && cadCliSnap.data().headerValue) {
            return cadCliSnap.data().headerValue;
        }
    } catch (err) {
        console.warn('Erro ao obter token do Firestore, usando fallback:', err);
    }

    return "Bearer eyJ0eXAiOiJKV1QiLCJhbGciOiJIUzUxMiJ9.eyJ1YyI6IjI1NSIsImlzcyI6InBhY2t1cCIsImRhdGEiOiIyMDI0LTA0LTE3VDEwOjIxOjAwLjEzNTk2NjYtMDM6MDAifQ.9R1LGKSx1gF2aC_6nWOmFOGZ4C-3BWuH1mE0-XOsBNmhZ-6n3TGx3X8XtGaXsVFmK744N2C95dXAOvLieW1wbQ";
}

/**
 * Carrega a lista completa de empresas da Alterdata (com suporte a paginação)
 */
export async function buscarEmpresasAlterdata({ token, onProgress }) {
    let offset = 0;
    const pageLimit = 100;
    let allEmpresas = [];
    const maxOffset = 5000; // salvaguarda

    while (offset <= maxOffset) {
        const url = `${ALTERDATA_BASE_URL}/empresas?page[limit]=${pageLimit}&page[offset]=${offset}`;
        const res = await fetch(url, {
            method: 'GET',
            headers: {
                'Authorization': token.startsWith('Bearer ') ? token : `Bearer ${token}`,
                'Accept': 'application/vnd.api+json'
            }
        });

        if (!res.ok) {
            throw new Error(`Erro na API Alterdata (Empresas): HTTP ${res.status}`);
        }

        const json = await res.json();
        const data = json.data || [];
        allEmpresas = allEmpresas.concat(data);

        if (onProgress) {
            onProgress({ carregadas: allEmpresas.length, total: json.meta?.totalResourceCount || allEmpresas.length });
        }

        if (data.length < pageLimit || allEmpresas.length >= (json.meta?.totalResourceCount || 0)) {
            break;
        }
        offset += pageLimit;
    }

    return allEmpresas.map(e => ({
        id: String(e.id),
        externoid: String(e.attributes?.externoid || '').trim(),
        nome: e.attributes?.nome || '',
        cnpj: higienizarDoc(e.attributes?.cpfCnpjAlfanumerico || e.attributes?.cpfcnpj || ''),
        ativa: e.attributes?.ativa !== false
    }));
}

/**
 * Consulta os funcionários ativos de uma empresa específica no Alterdata
 */
export async function buscarFuncionariosEmpresaAlterdata({ alterdataEmpresaId, token, pageLimit = 100 }) {
    const url = `${ALTERDATA_BASE_URL}/funcionarios?filter[empresaId]=${alterdataEmpresaId}&filter[status]=ativo&page[limit]=${pageLimit}`;
    const res = await fetch(url, {
        method: 'GET',
        headers: {
            'Authorization': token.startsWith('Bearer ') ? token : `Bearer ${token}`,
            'Accept': 'application/vnd.api+json'
        }
    });

    if (!res.ok) {
        throw new Error(`Erro na API Alterdata (Funcionários empresa ${alterdataEmpresaId}): HTTP ${res.status}`);
    }

    const json = await res.json();
    return {
        data: json.data || [],
        totalResourceCount: json.meta?.totalResourceCount || (json.data || []).length
    };
}

/**
 * Sincroniza colaboradores internos das Acads selecionadas
 */
export async function sincronizarEquipeInternaAcads({ db, DB_PATH, token: customToken, onProgress, currentUser }) {
    const t0 = Date.now();
    const token = customToken || await obterTokenAlterdata(db, DB_PATH);

    // 1. Obter lista de códigos de clientes Acads configuradas
    const configRef = doc(db, `${DB_PATH}hub_settings`, 'internal_team_config');
    const configSnap = await getDoc(configRef);
    const selectedClientIds = (configSnap.exists() && configSnap.data().selectedClientIds) || [
        "00001", "10033", "10342", "10343", "10413", "10480",
        "10576", "10582", "10589", "30212", "30277", "30501"
    ];
    const clientLabels = (configSnap.exists() && configSnap.data().clientLabels) || {};

    if (onProgress) onProgress({ status: 'Carregando empresas da Alterdata...', percent: 10 });
    const empresasAlterdata = await buscarEmpresasAlterdata({ token });

    // Mapear código externo (idDoCliente) para Alterdata ID
    const mapByExterno = {};
    const mapByCnpj = {};
    empresasAlterdata.forEach(e => {
        if (e.externoid) mapByExterno[e.externoid] = e;
        if (e.cnpj) mapByCnpj[e.cnpj] = e;
    });

    const matchingAcads = [];
    selectedClientIds.forEach(clientId => {
        const cleanId = String(clientId).trim();
        const found = mapByExterno[cleanId] || mapByExterno[cleanId.padStart(5, '0')];
        if (found) {
            matchingAcads.push({ clientId: cleanId, alterdataId: found.id, nome: found.nome, label: clientLabels[cleanId] || found.nome });
        }
    });

    let totalColaboradores = 0;
    const errors = [];

    // 2. Iterar por cada Acad correspondente e buscar funcionários
    for (let i = 0; i < matchingAcads.length; i++) {
        const acad = matchingAcads[i];
        const percent = 20 + Math.floor(((i + 1) / matchingAcads.length) * 70);
        if (onProgress) {
            onProgress({ 
                status: `Sincronizando ${acad.label || acad.nome} (${i + 1}/${matchingAcads.length})...`,
                percent 
            });
        }

        try {
            const resp = await buscarFuncionariosEmpresaAlterdata({ 
                alterdataEmpresaId: acad.alterdataId, 
                token, 
                pageLimit: 100 
            });

            for (const func of resp.data) {
                const attr = func.attributes || {};
                const docId = `acad_${acad.clientId}_${func.id}`;
                const colabData = {
                    id: docId,
                    idAlterdata: String(func.id),
                    idDoCliente: acad.clientId,
                    unidadeNome: acad.label || acad.nome,
                    matricula: attr.codigo || '',
                    nome: attr.nome || '',
                    cpf: attr.cpf ? String(attr.cpf) : '',
                    cargo: attr.nomefuncao || '',
                    salarioAtual: typeof attr.salarioBase === 'number' ? attr.salarioBase : Number(attr.salarioBase || 0),
                    status: attr.status || 'Ativo',
                    dataAdmissao: attr.admissao || null,
                    dataNascimento: attr.nascimento || null,
                    afastamento: attr.afastamento || null,
                    afastamentodescricao: attr.afastamentodescricao || '',
                    email: attr.email || '',
                    telefone: attr.telefone || '',
                    celular: attr.telefonecelular || '',
                    chavePix: attr.chavePix || null,
                    banco: attr.nomeDoBanco || (attr.banco ? String(attr.banco) : ''),
                    agencia: attr.agencia || '',
                    conta: attr.conta || '',
                    cidade: attr.cidade || '',
                    bairro: attr.bairro || '',
                    cep: attr.cep || '',
                    horarios: attr.horarios || null,
                    atualizadoEm: new Date().toISOString()
                };

                await setDoc(doc(db, `${DB_PATH}acad_colaboradores`, docId), colabData, { merge: true });
                totalColaboradores++;
            }
        } catch (err) {
            console.error(`Erro ao sincronizar Acad ${acad.clientId}:`, err);
            errors.push({ clientId: acad.clientId, erro: err.message });
        }
    }

    const duracaoMs = Date.now() - t0;
    const logData = {
        id: `log_${Date.now()}`,
        tipo: 'EQUIPE_INTERNA_ACADS',
        status: errors.length === 0 ? 'SUCESSO' : (matchingAcads.length > errors.length ? 'PARCIAL' : 'FALHA'),
        dataHora: new Date().toISOString(),
        totalAcadsConfiguradas: selectedClientIds.length,
        totalAcadsVinculadas: matchingAcads.length,
        totalColaboradores,
        erros: errors,
        duracaoMs,
        executadoPor: currentUser?.email || 'Administrador'
    };

    await setDoc(doc(db, `${DB_PATH}hub_integration_logs`, logData.id), logData);

    if (onProgress) onProgress({ status: 'Concluído com sucesso!', percent: 100 });
    return logData;
}

/**
 * Sincroniza o Headcount (quantidade de funcionários ativos) para todos os clientes ativos do apex-clientes
 */
export async function sincronizarHeadcountClientes({ db, DB_PATH, token: customToken, onProgress, currentUser }) {
    const t0 = Date.now();
    const token = customToken || await obterTokenAlterdata(db, DB_PATH);

    if (onProgress) onProgress({ status: 'Carregando empresas da Alterdata...', percent: 5 });
    const empresasAlterdata = await buscarEmpresasAlterdata({ token });

    const mapByExterno = {};
    const mapByCnpj = {};
    empresasAlterdata.forEach(e => {
        if (e.externoid) mapByExterno[e.externoid] = e.id;
        if (e.cnpj) mapByCnpj[e.cnpj] = e.id;
    });

    if (onProgress) onProgress({ status: 'Lendo clientes ativos do apex-clientes...', percent: 15 });
    const clientesSnap = await getDocs(collection(db, `${DB_PATH}clientes`));
    const clientesAtivos = clientesSnap.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .filter(c => c.clienteAtivo !== false);

    let totalAtualizados = 0;
    let totalSemVinculo = 0;
    const errors = [];

    const total = clientesAtivos.length;
    for (let i = 0; i < total; i++) {
        const cliente = clientesAtivos[i];
        const clientId = String(cliente.idDoCliente || cliente.id || '').trim();
        const clientCnpj = higienizarDoc(cliente.cnpj || cliente.cpfCnpj || '');

        // Tentar cruzar por idDoCliente ou por CNPJ
        const alterdataEmpresaId = mapByExterno[clientId] || 
                                   mapByExterno[clientId.padStart(5, '0')] || 
                                   (clientCnpj ? mapByCnpj[clientCnpj] : null);

        if (alterdataEmpresaId) {
            try {
                // page[limit]=1 para pegar apenas o totalResourceCount em milissegundos
                const resp = await buscarFuncionariosEmpresaAlterdata({ 
                    alterdataEmpresaId, 
                    token, 
                    pageLimit: 1 
                });

                const count = resp.totalResourceCount;
                await setDoc(doc(db, `${DB_PATH}clientes`, cliente.id), {
                    estatisticasDp: {
                        alterdataEmpresaId,
                        totalFuncionariosAtivos: count,
                        atualizadoEm: new Date().toISOString()
                    }
                }, { merge: true });

                totalAtualizados++;
            } catch (err) {
                errors.push({ clienteId: clientId, alterdataEmpresaId, erro: err.message });
            }
        } else {
            totalSemVinculo++;
        }

        if (i % 10 === 0 || i === total - 1) {
            const percent = 15 + Math.floor(((i + 1) / total) * 80);
            if (onProgress) {
                onProgress({
                    status: `Processando clientes (${i + 1}/${total}) - ${totalAtualizados} atualizados...`,
                    percent,
                    processados: i + 1,
                    total,
                    atualizados: totalAtualizados
                });
            }
        }
    }

    const duracaoMs = Date.now() - t0;

    // Atualizar agendamento / última execução
    const hcRef = doc(db, `${DB_PATH}hub_settings`, 'headcount_sync_config');
    const hcSnap = await getDoc(hcRef);
    const intervalHours = (hcSnap.exists() && hcSnap.data().intervalHours) || 24;

    const lastSummary = {
        totalClientesProcessados: total,
        totalAtualizados,
        totalSemVinculo,
        duracaoMs,
        data: new Date().toISOString()
    };

    await setDoc(hcRef, {
        id: 'headcount_sync_config',
        status: errors.length === 0 ? 'SUCCESS' : 'WARNING',
        lastRun: new Date().toISOString(),
        nextRun: new Date(Date.now() + intervalHours * 3600000).toISOString(),
        lastSummary,
        updatedAt: new Date().toISOString(),
        updatedBy: currentUser?.email || 'Manual'
    }, { merge: true });

    // Gravar log de monitoramento
    const logData = {
        id: `log_${Date.now()}`,
        tipo: 'HEADCOUNT_CLIENTES',
        status: errors.length === 0 ? 'SUCESSO' : (totalAtualizados > 0 ? 'PARCIAL' : 'FALHA'),
        dataHora: new Date().toISOString(),
        totalProcessados: total,
        totalAtualizados,
        totalSemVinculo,
        erros: errors.slice(0, 50), // salva primeiros 50 erros se houver
        duracaoMs,
        executadoPor: currentUser?.email || 'Manual'
    };

    await setDoc(doc(db, `${DB_PATH}hub_integration_logs`, logData.id), logData);

    if (onProgress) onProgress({ status: 'Sincronização de Headcount concluída!', percent: 100 });
    return logData;
}
